import { Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";
import { LanguageToggle } from "@/components/language-toggle";
import { Logo } from "@/components/logo";
import { SiteFooter } from "@/components/site-footer";
import { ThemeToggle } from "@/components/theme-toggle";
import { useI18n } from "@/i18n";
import { LEGAL } from "./content";

export function LegalPage({ doc }: { doc: "terms" | "privacy" }) {
  const { locale, t } = useI18n();
  const content = LEGAL[doc][locale];

  useEffect(() => {
    document.title = `${content.title} · Zeeta`;
    return () => {
      document.title = "Zeeta";
    };
  }, [content.title]);

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="flex items-center justify-between border-b px-4 py-3 sm:px-6">
        <Link to="/" aria-label="Zeeta">
          <Logo />
        </Link>
        <div className="flex items-center gap-1">
          <ThemeToggle />
          <LanguageToggle />
        </div>
      </header>
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-10 sm:px-6">
        <Link
          to="/"
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden />
          {t("common.back")}
        </Link>
        <article className="space-y-6">
          <header className="space-y-2">
            <h1 className="text-3xl font-semibold tracking-tight">{content.title}</h1>
            <p className="text-sm text-muted-foreground">{content.updated}</p>
          </header>
          <p className="leading-relaxed">{content.intro}</p>
          {content.sections.map((section) => (
            <section key={section.heading} className="space-y-3">
              <h2 className="text-lg font-semibold">{section.heading}</h2>
              {section.paragraphs.map((p) => (
                <p key={p} className="leading-relaxed text-foreground/90">
                  {p}
                </p>
              ))}
            </section>
          ))}
        </article>
      </main>
      <SiteFooter />
    </div>
  );
}
